import type { ReactElement, ReactNode, SyntheticEvent } from "react"
import Link from "@onestore/hel/dist/components/quarks/Link"
import type { CtaElement } from "@gatsby-plugin-generic-page/fragments/ctaElement"
import { getCtaButtonFlatData } from "@gatsby-plugin-generic-page/helpers/getCtaElementFlatData"
import { getStaticPrices } from "@gatsby-plugin-generic-page/helpers/staticPrices"
import useProductBoxTitleWithLabels from "@gatsby-plugin-generic-page/hooks/useProductBoxTitleWithLabels"
import { CardTariff } from "@ionos-web-design-system/react"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { StaticPrice } from "~/fragments/staticPrice"
import useAnchorProperties from "~/hooks/useAnchorProperties"
import { sendGaEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import { getDetailedStaticPriceData } from "../DetailedStaticPrice/helpers/getDetailedStaticPriceData"
import type { ProductType } from "."
import ProductBoxButton from "./ProductBoxButton"
import ProductBoxSlimFooter from "./ProductBoxFooter"

export type ProductBoxWithCtaAnchorProps = {
  product: ProductType
  anchorId: string
  cta: CtaElement[]
  staticPrices?: StaticPrice[] | null
}

export default function ProductBoxWithCtaAnchor({
  product,
  anchorId,
  cta,
  staticPrices,
}: ProductBoxWithCtaAnchorProps): ReactElement<ProductBoxWithCtaAnchorProps> {
  const { currentPriceType } = usePriceTypeContext()

  const titleWithLabels = useProductBoxTitleWithLabels(
    product.title,
    product.discountLabel,
    product.labels
  )

  const prices = staticPrices
    ? getStaticPrices(staticPrices, currentPriceType)
    : {}

  const { text, title, gaEvent } = getCtaButtonFlatData(
    cta[0],
    currentPriceType
  )

  const { href, onClick } = useAnchorProperties(anchorId)

  const period =
    Object.keys(prices).length === 1
      ? Object.keys(prices)[0]
      : product.period || Object.keys(prices)[0]

  const handleOnClick = (event: SyntheticEvent) => {
    sendGaEvent(gaEvent)

    onClick(event)
  }

  const staticPriceResult =
    !isEmpty(staticPrices) && period
      ? getDetailedStaticPriceData(
          staticPrices,
          period,
          currentPriceType,
          product.additionalText,
          undefined,
          true
        )
      : null

  const buttonNode: ReactNode = (
    <Link
      title={title}
      href={href}
      onClick={handleOnClick}
      className="contents"
    >
      <ProductBoxButton concept={product.concept} variant={product.variant}>
        {text}
      </ProductBoxButton>
    </Link>
  )

  return (
    <CardTariff
      title={titleWithLabels}
      subtitle={product.description}
      badgeText={product.promoText}
      priceData={
        !isEmpty(staticPriceResult) && !isEmpty(staticPriceResult.data)
          ? { ...staticPriceResult.data, reservePrelineSpace: true }
          : undefined
      }
      button={buttonNode}
      className="leading-normal"
    >
      <div className="flex flex-col gap-gap-8 h-full">
        {product.content.parameters}

        {!isEmpty(product.content?.footer) ? (
          <ProductBoxSlimFooter
            {...product.content.footer}
            setIsChecked={() => {}}
            isChecked={false}
          />
        ) : null}
      </div>
    </CardTariff>
  )
}
